/**
 * SubscriptionConfigCard Component
 *
 * Dashboard Stats-Grid with 3 tiles: Plan & Status, Usage, Next Billing.
 * Adapts between credit-based (Starter) and dimension-based (Pro+) views.
 *
 * WCAG 2.1 Level AA + BFSG compliant
 */

import { useState } from 'react'
import { AlertTriangle, CalendarClock, CreditCard, TrendingUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent } from '@/components/v2/ui/Card'
import { Alert, AlertDescription } from '@/components/v2/ui'
import { Progress } from '@/components/v2/ui/Progress'
import { Badge } from '@/components/v2/ui/Badge'
import { useSubscriptionConfig } from '@/hooks/subscription/useSubscriptionConfig'
import { useSubscriptionUsage } from '@/hooks/subscription/useSubscriptionUsage'
import { useActiveSubscription } from '@/hooks/subscription/useActiveSubscription'
import { cancelScheduledDowngrade, cancelSubscription, reactivateSubscription } from '@/lib/api/subscription'
import { DowngradeBanner } from './DowngradeBanner'
import { CancelSubscriptionDialog } from './CancelSubscriptionDialog'
import { Button } from '@/components/v2/ui/Button'
import { formatCurrency, formatDate, formatNumber } from '@/lib/utils'
import type { SubscriptionConfig } from '@/lib/api/subscription-pricing'
import type { UsageInfo } from '@/lib/api/subscription-pricing'
import type { ScheduledDowngrade } from '@/api/subscriptions'

function PlanStatusTile({
  config,
  usage,
  scheduledDowngrade,
  onCancelDowngrade,
  cancelingDowngrade,
  isCanceled,
  onCancel,
  onReactivate,
  actionLoading,
}: {
  config: SubscriptionConfig
  usage: UsageInfo | undefined
  scheduledDowngrade: ScheduledDowngrade | null | undefined
  onCancelDowngrade: () => void
  cancelingDowngrade: boolean
  isCanceled: boolean
  onCancel: () => void
  onReactivate: () => void
  actionLoading: boolean
}) {
  const { t } = useTranslation()
  const tierName = config.pricing_model === 'credit' ? 'Starter' : 'Pro'
  const showCanceled = isCanceled || config.status === 'canceled'
  const badgeVariant = showCanceled ? 'default' as const : ({
    active: 'success' as const,
    pending: 'warning' as const,
    canceled: 'default' as const,
  }[config.status] ?? ('default' as const))
  const statusLabel = showCanceled ? t('billing.statusCanceled') : ({
    active: t('billing.statusActive'),
    pending: t('billing.statusPending'),
    canceled: t('billing.statusCanceled'),
  }[config.status] ?? config.status)

  const downgradeDate = scheduledDowngrade
    ? new Date(scheduledDowngrade.effective_date * 1000).toLocaleDateString('de-DE')
    : null

  const showCancel = config.status === 'active' && !isCanceled && !scheduledDowngrade

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-neutral-500 dark:text-neutral-400">
            <CreditCard className="h-4 w-4" aria-hidden="true" />
            <span className="text-xs font-medium uppercase tracking-wider">{t('billing.planAndStatus')}</span>
          </div>
          {showCancel && (
            <Button variant="primary" size="sm" onClick={onCancel} loading={actionLoading}>
              {t('billing.cancelSubscription')}
            </Button>
          )}
          {isCanceled && (
            <Button variant="outline" size="sm" onClick={onReactivate} loading={actionLoading}>
              {t('billing.reactivateSubscription')}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-3 mb-2">
          <span className="text-xl font-bold text-neutral-900 dark:text-neutral-50">
            {tierName}
          </span>
          <Badge variant={badgeVariant} size="sm">{statusLabel}</Badge>
        </div>
        <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
          {formatCurrency(config.monthly_price_eur)}
          <span className="text-sm font-normal text-neutral-500 dark:text-neutral-400">
            {t('billing.perMonth')}
          </span>
        </p>
        <div className="mt-3 space-y-1 text-sm text-neutral-600 dark:text-neutral-400">
          {usage?.credits && (
            <p>{formatNumber(usage.credits.limit)} Credits / {t('billing.perMonth')}</p>
          )}
          {config.product_limit != null && (
            <p>
              {formatNumber(config.product_limit)} {t('billing.products')}
            </p>
          )}
          {config.channel_count != null && (
            <p>
              {t('billing.channelsIncluded', { count: config.channel_count })}
            </p>
          )}
        </div>
        {scheduledDowngrade && (
          <DowngradeBanner
            toTier={scheduledDowngrade.to_tier_code}
            toPrice={scheduledDowngrade.to_price_eur}
            date={downgradeDate!}
            onCancel={onCancelDowngrade}
            loading={cancelingDowngrade}
          />
        )}
      </CardContent>
    </Card>
  )
}

function DimensionUsageTile({ usage }: { usage: UsageInfo }) {
  const { t } = useTranslation()
  const productPct = usage.products.percentage
  const productVariant = productPct > 85 ? 'error' : productPct > 60 ? 'warning' : 'success'

  const creditPct = Math.min(usage.credits?.percentage ?? 0, 100)
  const creditVariant = creditPct > 85 ? 'error' : creditPct > 60 ? 'warning' : 'success'

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-4 text-neutral-500 dark:text-neutral-400">
          <TrendingUp className="h-4 w-4" aria-hidden="true" />
          <span className="text-xs font-medium uppercase tracking-wider">{t('billing.usageTitle')}</span>
        </div>
        <div className="mb-1">
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-2xl font-bold text-neutral-900 dark:text-neutral-50">
              {productPct.toFixed(0)}%
            </span>
            <span className="text-sm text-neutral-500 dark:text-neutral-400">
              {formatNumber(usage.products.current)} / {formatNumber(usage.products.limit)} {t('billing.products')}
            </span>
          </div>
          <Progress
            value={productPct}
            variant={productVariant}
            size="md"
            aria-label={t('billing.productsUsedAria', { percent: productPct.toFixed(0) })}
          />
        </div>

        {usage.credits && (
          <div className="mt-3">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
                {creditPct.toFixed(0)}%
              </span>
              <span className="text-sm text-neutral-500 dark:text-neutral-400">
                {formatNumber(Math.min(usage.credits.current, usage.credits.limit))} / {formatNumber(usage.credits.limit)} Credits
              </span>
            </div>
            <Progress
              value={creditPct}
              variant={creditVariant}
              size="sm"
              aria-label={t('billing.creditsUsedAria', { percent: creditPct.toFixed(0) })}
            />
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function NextBillingTile({
  config,
  isCanceled,
  periodEnd,
}: {
  config: SubscriptionConfig
  isCanceled: boolean
  periodEnd: string | null
}) {
  const { t } = useTranslation()
  const billingDate = isCanceled ? periodEnd : config.valid_until
  const label = isCanceled ? t('billing.canceledOn') : t('billing.nextBilling')

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 mb-4 text-neutral-500 dark:text-neutral-400">
          <CalendarClock className="h-4 w-4" aria-hidden="true" />
          <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
        </div>
        <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-50 mb-1">
          {billingDate ? formatDate(billingDate) : '–'}
        </p>
        {!isCanceled && (
          <p className="text-lg text-neutral-600 dark:text-neutral-300">
            {formatCurrency(config.monthly_price_eur)}
          </p>
        )}
      </CardContent>
    </Card>
  )
}

export function SubscriptionConfigCard() {
  const { t } = useTranslation()
  const { data: config, isLoading: configLoading, error: configError, refetch: refetchConfig } = useSubscriptionConfig()
  const { data: usage, isLoading: usageLoading } = useSubscriptionUsage()
  const { data: activeSubscription, refetch: refetchSubscription } = useActiveSubscription()
  const [cancelingDowngrade, setCancelingDowngrade] = useState(false)
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  const scheduledDowngrade = activeSubscription?.scheduled_downgrade ?? null
  const isCanceled = activeSubscription?.cancel_at_period_end ?? false
  const periodEnd = activeSubscription?.current_period_end ?? null

  const refetchAll = async () => {
    await Promise.all([refetchSubscription(), refetchConfig()])
  }

  const handleCancelDowngrade = async () => {
    setCancelingDowngrade(true)
    try {
      await cancelScheduledDowngrade()
      await refetchAll()
    } catch {
      // Error handled by API client
    } finally {
      setCancelingDowngrade(false)
    }
  }

  const handleCancelSubscription = async () => {
    setActionLoading(true)
    try {
      await cancelSubscription()
      await refetchAll()
      setCancelDialogOpen(false)
    } catch {
      // Error handled by API client
    } finally {
      setActionLoading(false)
    }
  }

  const handleReactivate = async () => {
    setActionLoading(true)
    try {
      await reactivateSubscription()
      await refetchAll()
    } catch {
      // Error handled by API client
    } finally {
      setActionLoading(false)
    }
  }

  if (configLoading || usageLoading) {
    return (
      <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="pt-6">
              <div className="animate-pulse space-y-3">
                <div className="h-3 bg-neutral-200 dark:bg-neutral-800 rounded w-1/3" />
                <div className="h-6 bg-neutral-200 dark:bg-neutral-800 rounded w-2/3" />
                <div className="h-4 bg-neutral-200 dark:bg-neutral-800 rounded w-1/2" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  if (configError || !config) {
    return (
      <Alert variant="info">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          {t('billing.noSubscriptionConfigured')} {t('billing.useCalculatorHint')}
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <>
      <div className="grid gap-4 grid-cols-1 md:grid-cols-3">
        <PlanStatusTile
          config={config}
          usage={usage}
          scheduledDowngrade={scheduledDowngrade}
          onCancelDowngrade={handleCancelDowngrade}
          cancelingDowngrade={cancelingDowngrade}
          isCanceled={isCanceled}
          onCancel={() => setCancelDialogOpen(true)}
          onReactivate={handleReactivate}
          actionLoading={actionLoading}
        />
        {usage ? (
          <DimensionUsageTile usage={usage} />
        ) : (
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                {t('billing.noUsageData')}
              </p>
            </CardContent>
          </Card>
        )}
        <NextBillingTile config={config} isCanceled={isCanceled} periodEnd={periodEnd} />
      </div>

      <CancelSubscriptionDialog
        open={cancelDialogOpen}
        onOpenChange={setCancelDialogOpen}
        periodEnd={periodEnd ?? config.valid_until ?? ''}
        onConfirm={handleCancelSubscription}
        loading={actionLoading}
      />
    </>
  )
}
